import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Upload, ScanLine, Loader2, CheckCircle2, X, Zap,
  Download, ArrowRight, Clock, Landmark, FileText
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { fmt, API_URL } from './constants';
import { TaxReturnResults } from './TaxResults';

function ScannerTab({ workspaceId, onAnalyzeTaxReturn }) {
  const [docType, setDocType] = useState('bank_statement');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState(null);
  const [editedTransactions, setEditedTransactions] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [taxJobId, setTaxJobId] = useState(null);
  const [taxProgress, setTaxProgress] = useState({ pages_processed: 0, pages_total: 0 });
  const [taxEstSeconds, setTaxEstSeconds] = useState(0);
  const [taxStartTime, setTaxStartTime] = useState(null);
  const [scanAll, setScanAll] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const [taxData, setTaxData] = useState(null);
  const [scanCredits, setScanCredits] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [taxAnalysis, setTaxAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const CATEGORIES = [
    'Income', 'Transfer', 'Rent/Mortgage', 'Utilities', 'Insurance',
    'Groceries', 'Dining', 'Transportation', 'Shopping', 'Healthcare',
    'Entertainment', 'Subscriptions', 'Payroll', 'Taxes', 'Loan Payment',
    'Credit Card Payment', 'ATM Withdrawal', 'Fee', 'Other'
  ];

  // Clean up polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Fetch scan credits (user-based)
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/billing/credits`);
        if (res.ok) setScanCredits(await res.json());
      } catch { /* ignore */ }
    };
    fetchCredits();
  }, [extracting]);

  // Fetch scan history and auto-resume in-progress jobs
  const fetchScanHistory = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingHistory(true);
    try {
      const res = await apiFetch(`${API_URL}/api/ocr/scan-jobs/${workspaceId}`);
      if (res.ok) {
        const data = await res.json();
        setScanHistory(data.jobs || []);

        // Auto-resume polling for any in-progress/preparing job
        const activeJob = (data.jobs || []).find(j => j.status === 'processing' || j.status === 'preparing');
        if (activeJob && !taxJobId && !extracting) {
          setTaxJobId(activeJob.job_id);
          setExtracting(true);
          setDocType(activeJob.doc_type || 'tax_return');
          setTaxProgress({ pages_processed: activeJob.pages_processed || 0, pages_total: activeJob.pages_total || 0 });
          setTaxEstSeconds(activeJob.est_seconds || 30);
          setTaxStartTime(Date.now());
          if (activeJob.pages_skipped > 0 || activeJob.pages_capped) {
            setScanInfo({ pages_original: activeJob.pages_original, pages_skipped: activeJob.pages_skipped, pages_capped: activeJob.pages_capped, pages_total: activeJob.pages_total });
          }
          pollTaxJob(activeJob.job_id);
        }
      }
    } catch { /* ignore */ }
    setLoadingHistory(false);
  }, [workspaceId, taxJobId, extracting]);

  useEffect(() => { fetchScanHistory(); }, [fetchScanHistory]);

  // Fetch bank accounts for import target selection
  useEffect(() => {
    if (!workspaceId) return;
    apiFetch(`${API_URL}/api/banking/accounts?workspace_id=${workspaceId}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setBankAccounts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [workspaceId]);


  // Load a completed scan job's full results
  const loadJobResults = async (jobId) => {
    try {
      const res = await apiFetch(`${API_URL}/api/ocr/scan-job/${jobId}`);
      if (res.ok) {
        const job = await res.json();
        setSelectedJob(job);
        if (job.doc_type === 'tax_return' && job.tax_data) {
          setTaxData(job.tax_data);
          setDocType('tax_return');
        } else if (job.doc_type === 'bank_statement' && job.transactions) {
          setResult(job);
          setEditedTransactions((job.transactions || []).map((t, i) => ({ ...t, _id: i, _selected: true })));
          setDocType('bank_statement');
        } else if (job.doc_type === 'invoice' && job.line_items) {
          setResult(job);
          setDocType('invoice');
        }
      }
    } catch { /* ignore */ }
  };

  const handleFile = (f) => {
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(f.type) && !f.name.match(/\.(pdf|png|jpg|jpeg)$/i)) {
      alert('Please upload a PDF, PNG, or JPG file.');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      alert('File too large. Maximum 20MB.');
      return;
    }
    setFile(f);
    setResult(null);
    setEditedTransactions([]);
    setImportResult(null);
    setTaxData(null);
    setTaxJobId(null);
    setScanAll(false);
    setScanInfo(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFile(e.dataTransfer.files[0]);
  };

  const handleCancelScan = async () => {
    if (!taxJobId) return;
    try {
      await apiFetch(`${API_URL}/api/ocr/scan-job/${taxJobId}/cancel`, { method: 'POST' });
    } catch { /* ignore */ }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setExtracting(false);
    setTaxJobId(null);
    setTaxStartTime(null);
  };

  const pollTaxJob = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/ocr/scan-job/${jobId}`);
        if (!res.ok) return;
        const job = await res.json();
        setTaxProgress({ pages_processed: job.pages_processed || 0, pages_total: job.pages_total || 0 });

        // Update scan info when job moves from preparing to processing
        if (job.status === 'processing' && job.pages_original > 0) {
          setScanInfo({
            pages_original: job.pages_original,
            pages_skipped: job.pages_skipped,
            pages_capped: job.pages_capped,
            pages_total: job.pages_total,
          });
          if (job.est_seconds) setTaxEstSeconds(job.est_seconds);
          if (!taxStartTime) setTaxStartTime(Date.now());
        }

        if (job.status === 'complete') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Route results based on document type
          if (job.doc_type === 'tax_return') {
            setTaxData(job.tax_data || {});
          } else if (job.doc_type === 'bank_statement') {
            setResult({ transactions: job.transactions || [], transaction_count: job.transaction_count || 0 });
            setEditedTransactions((job.transactions || []).map((t, i) => ({ ...t, _id: i, _selected: true })));
          } else {
            setResult({ line_items: job.line_items || [], transaction_count: job.transaction_count || 0 });
          }
          setExtracting(false);
          setTaxStartTime(null);
          fetchScanHistory();
        } else if (job.status === 'failed' || job.status === 'cancelled') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setExtracting(false);
          setTaxStartTime(null);
          fetchScanHistory();
          if (job.status === 'failed') {
            alert(job.error_message || job.error || 'Document extraction failed. Please try again.');
          }
        } else if (job.status === 'partial_error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (job.doc_type === 'tax_return') {
            setTaxData(job.tax_data || {});
          } else if (job.doc_type === 'bank_statement') {
            setResult({ transactions: job.transactions || [], transaction_count: job.transaction_count || 0 });
            setEditedTransactions((job.transactions || []).map((t, i) => ({ ...t, _id: i, _selected: true })));
          } else {
            setResult({ line_items: job.line_items || [], transaction_count: job.transaction_count || 0 });
          }
          setExtracting(false);
          setTaxStartTime(null);
          fetchScanHistory();
          alert('Partial results available. ' + (job.error || ''));
        }
      } catch {
        // Continue polling on network errors
      }
    }, 2000);
  }, [taxStartTime]);

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    setResult(null);
    setImportResult(null);
    setTaxData(null);
    setScanInfo(null);
    setTaxAnalysis(null);
    setAnalysisError(null);

    try {
      // All doc types use the unified async scan-job flow
      const formData = new FormData();
      formData.append('file', file);
      formData.append('workspace_id', workspaceId);
      formData.append('doc_type', docType);
      if (docType === 'tax_return' && scanAll) formData.append('scan_all', 'true');

      const response = await apiFetch(`${API_URL}/api/ocr/scan-job`, { method: 'POST', body: formData });
      if (response.ok) {
        const data = await response.json();
        setTaxJobId(data.job_id);
        setTaxProgress({ pages_processed: 0, pages_total: data.pages_total || 1 });
        setTaxEstSeconds(data.est_seconds || 30);
        setTaxStartTime(Date.now());
        setScanInfo({
          pages_original: data.pages_original,
          pages_skipped: data.pages_skipped,
          pages_capped: data.pages_capped,
          pages_total: data.pages_total,
        });
        pollTaxJob(data.job_id);
      } else {
        const err = await response.json().catch(() => ({}));
        setExtracting(false);
        if (response.status === 402) {
          setShowUpgradeModal(true);
        } else {
          alert(err.detail || 'Failed to start scan. Please try again.');
        }
      }
    } catch (error) {
      setExtracting(false);
      alert('Scan failed. Please check your connection and try again.');
    }
  };

  const toggleTransaction = (idx) => {
    setEditedTransactions(prev => prev.map((t, i) => i === idx ? { ...t, _selected: !t._selected } : t));
  };

  const updateTransaction = (idx, field, value) => {
    setEditedTransactions(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const handleImport = async () => {
    const selected = editedTransactions.filter(t => t._selected);
    if (selected.length === 0) { alert('No transactions selected for import.'); return; }

    const jobId = taxJobId || selectedJob?.job_id;
    if (!jobId) { alert('No scan job found. Please re-scan the document.'); return; }

    setImporting(true);
    try {
      const response = await apiFetch(`${API_URL}/api/ocr/scan-job/${jobId}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          transactions: selected.map(({ _id, _selected, ...t }) => t),
          account_info: result?.account_info || {},
          bank_id: selectedBankId || undefined,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setImportResult(data);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Import failed');
      }
    } catch (error) {
      alert('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setEditedTransactions([]);
    setImportResult(null);
    setTaxData(null);
    setTaxJobId(null);
    setScanAll(false);
    setScanInfo(null);
    setTaxAnalysis(null);
    setAnalyzing(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAnalyzeTaxReturn = async () => {
    if (!taxData || analyzing) return;
    setAnalyzing(true);
    setTaxAnalysis(null);
    setAnalysisError(null);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/analyze-tax-return`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tax_data: taxData, scan_job_id: taxJobId || selectedJob?.job_id }),
      });
      if (res.ok) {
        const data = await res.json();
        const aid = data.analysis_id;
        // Poll for completion
        let pollActive = true;
        const poll = setInterval(async () => {
          if (!pollActive) return;
          try {
            const pollRes = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/cfo-buck/tax-analysis/${aid}`);
            if (pollRes.ok) {
              const pollData = await pollRes.json();
              if (pollData.status === 'complete' && pollData.analysis) {
                pollActive = false;
                clearInterval(poll);
                setTaxAnalysis(pollData.analysis);
                setAnalyzing(false);
              } else if (pollData.status === 'failed') {
                pollActive = false;
                clearInterval(poll);
                setAnalyzing(false);
                const errMsg = (pollData.error || '').includes('Budget')
                  ? 'Your AI analysis budget has been exceeded. Please go to Profile > Universal Key > Add Balance to continue using this feature.'
                  : (pollData.error || 'Analysis failed. Please try again.');
                setAnalysisError(errMsg);
              }
            }
          } catch { /* continue polling */ }
        }, 3000);
        // Timeout after 3 minutes
        setTimeout(() => { if (pollActive) { pollActive = false; clearInterval(poll); setAnalyzing(false); setAnalysisError('Analysis timed out. Please try again.'); } }, 180000);
      } else {
        const err = await res.json().catch(() => ({}));
        setAnalyzing(false);
        setAnalysisError(err.detail || 'Failed to start analysis. Please try again.');
      }
    } catch (e) {
      console.error('Tax analysis error:', e);
      setAnalyzing(false);
      setAnalysisError('Failed to connect. Please check your connection and try again.');
    }
  };

  const selectedCount = editedTransactions.filter(t => t._selected).length;

  const DOC_TYPES = [
    { id: 'bank_statement', label: 'Bank Statement', icon: Landmark, desc: 'Extract transactions from bank or credit card statements' },
    { id: 'invoice', label: 'Invoice / Bill', icon: FileText, desc: 'Extract line items from invoices and bills' },
    { id: 'tax_return', label: 'Tax Return', icon: FileText, desc: 'Analyze prior year returns for missed savings' },
  ];

  return (
    <div className="space-y-6" data-testid="scanner-tab">
      {/* Credit Balance */}
      {scanCredits && (
        <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-muted border text-sm" data-testid="scan-credits-bar">
          <div className="flex items-center gap-3">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">{scanCredits.total_available}</span> credits remaining
            </span>
            {scanCredits.plan?.id !== 'none' && (
              <span className="text-muted-foreground text-xs">
                ({scanCredits.monthly_credits ?? scanCredits.credits_remaining ?? 0} monthly
                {(scanCredits.purchased_credits ?? scanCredits.bonus_credits ?? 0) > 0 && (
                  <> + {scanCredits.purchased_credits ?? scanCredits.bonus_credits ?? 0} purchased</>
                )})
              </span>
            )}
            {scanCredits.plan?.id === 'none' && (
              <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-600">No Plan</Badge>
            )}
            {scanCredits.plan?.id !== 'none' && (
              <span className="text-muted-foreground text-xs">{scanCredits.plan?.name}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(scanCredits.total_available < 10 || scanCredits.plan?.id === 'none') && (
              <a href="/settings?tab=billing" className="text-xs text-emerald-600 hover:underline font-medium" data-testid="get-more-credits-link">
                {scanCredits.plan?.id === 'none' ? 'Subscribe' : 'Get more credits'}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Credit Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              {scanCredits?.plan?.id === 'none' ? 'Subscribe to Start Scanning' : 'Out of Scan Credits'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {scanCredits?.plan?.id === 'none'
              ? 'Choose a plan to unlock document scanning, bank connections, and AI analysis.'
              : 'Your monthly credits have been used up. Upgrade your plan for more credits, or buy a one-time credit pack.'}
          </p>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {[
              { id: 'starter', name: 'Starter', price: '$19.99', credits: '50', banks: '3' },
              { id: 'professional', name: 'Professional', price: '$19.99', credits: '200', banks: '3', popular: true },
              { id: 'business', name: 'Business', price: '$34.99', credits: '750', banks: '8' },
            ].map(plan => {
              const isCurrent = scanCredits?.plan?.id === plan.id;
              return (
                <div
                  key={plan.id}
                  className={cn(
                    "rounded-xl border p-4 flex flex-col relative transition-all",
                    isCurrent ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10" : "border-border",
                    plan.popular && !isCurrent ? "ring-2 ring-emerald-500/30" : ""
                  )}
                  data-testid={`upgrade-plan-${plan.id}`}
                >
                  {plan.popular && !isCurrent && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                      <Badge className="bg-emerald-600 text-white text-[9px] px-2">Popular</Badge>
                    </div>
                  )}
                  {isCurrent && (
                    <Badge variant="outline" className="absolute -top-2.5 right-2 border-emerald-500 text-emerald-600 text-[9px]">Current</Badge>
                  )}
                  <p className="font-semibold text-sm">{plan.name}</p>
                  <p className="text-xl font-bold mt-1">{plan.price}<span className="text-xs font-normal text-muted-foreground">/mo</span></p>
                  <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-amber-500" />{plan.credits} scans/month</div>
                    <div className="flex items-center gap-1.5"><Landmark className="w-3 h-3 text-blue-500" />{plan.banks} bank connections</div>
                  </div>
                  <div className="mt-3">
                    {isCurrent ? (
                      <Button size="sm" variant="outline" className="w-full text-xs" disabled>Current Plan</Button>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => { setShowUpgradeModal(false); window.location.href = '/settings?tab=billing'; }}
                        data-testid={`upgrade-select-${plan.id}`}
                      >
                        {isCurrent ? 'Current' : (scanCredits?.plan?.id === 'none' ? 'Subscribe' : 'Upgrade')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-2 pt-3 border-t">
            <p className="text-xs text-muted-foreground">
              Or buy a <a href="/settings?tab=billing" className="text-emerald-600 font-medium hover:underline" onClick={() => setShowUpgradeModal(false)}>credit pack</a> — purchased credits roll over.
            </p>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowUpgradeModal(false)}>
              Maybe later
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Type Selector */}
      <div>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2.5 block">Document Type</span>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {DOC_TYPES.map(dt => (
            <button key={dt.id} onClick={() => { setDocType(dt.id); handleReset(); }}
              className={cn("flex items-start gap-3 p-4 rounded-xl text-left transition-all border",
                docType === dt.id
                  ? "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-300 dark:border-emerald-700 shadow-sm"
                  : "bg-card border-border hover:border-primary/30 dark:hover:border-primary/30"
              )} data-testid={`doc-type-${dt.id}`}>
              <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0",
                docType === dt.id ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted"
              )}>
                <dt.icon className={cn("w-4.5 h-4.5", docType === dt.id ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")} />
              </div>
              <div>
                <p className={cn("text-sm font-semibold", docType === dt.id ? "text-emerald-700 dark:text-emerald-400" : "text-foreground")}>
                  {dt.label}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{dt.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Upload Area — hide when results are showing */}
      {!result && !importResult && !taxData && (
        <Card className="border-2 border-dashed border-border bg-muted/50">
          <CardContent className="p-8">
            <div
              className={cn("flex flex-col items-center justify-center gap-4 py-8 rounded-xl transition-colors cursor-pointer",
                dragOver && "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-400")}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !extracting && fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden"
                onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} data-testid="file-input" />

              {extracting ? (
                <>
                  <div className="relative">
                    <Loader2 className="w-14 h-14 text-emerald-500 animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-emerald-600">{taxProgress.pages_processed}/{taxProgress.pages_total}</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-foreground">
                      {taxProgress.pages_total === 0 ? 'Preparing document...' : `Analyzing ${docType === 'tax_return' ? 'Tax Return' : docType === 'bank_statement' ? 'Bank Statement' : 'Invoice'} with AI...`}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {taxProgress.pages_total === 0
                        ? 'Converting PDF and filtering blank pages...'
                        : <>Page {taxProgress.pages_processed} of {taxProgress.pages_total} processed{taxProgress.pages_total > 5 && ' (5 pages at a time)'}</>
                      }
                    </p>
                    {scanInfo && (scanInfo.pages_skipped > 0 || scanInfo.pages_capped) && (
                      <p className="text-[11px] text-amber-500 mt-1">
                        {scanInfo.pages_skipped > 0 && `${scanInfo.pages_skipped} blank page${scanInfo.pages_skipped > 1 ? 's' : ''} skipped`}
                        {scanInfo.pages_skipped > 0 && scanInfo.pages_capped && ' · '}
                        {scanInfo.pages_capped && `Capped at ${scanInfo.pages_total} of ${scanInfo.pages_original} pages`}
                      </p>
                    )}
                    <div className="w-56 h-2 rounded-full bg-muted mt-3 mx-auto overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                        style={{ width: `${taxProgress.pages_total > 0 ? (taxProgress.pages_processed / taxProgress.pages_total) * 100 : 0}%` }} />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {taxEstSeconds > 0 && taxStartTime ? (() => {
                        const elapsed = Math.floor((Date.now() - taxStartTime) / 1000);
                        const remaining = Math.max(0, taxEstSeconds - elapsed);
                        if (remaining > 60) return `~${Math.ceil(remaining / 60)} min remaining`;
                        if (remaining > 0) return `~${remaining}s remaining`;
                        return 'Almost done...';
                      })() : `Est. ${taxEstSeconds > 60 ? Math.ceil(taxEstSeconds / 60) + ' min' : taxEstSeconds + 's'}`}
                    </p>
                    <Button
                      variant="outline" size="sm"
                      className="mt-3 text-red-500 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={(e) => { e.stopPropagation(); handleCancelScan(); }}
                      data-testid="cancel-scan-btn"
                    >
                      <X className="w-3 h-3 mr-1" /> Stop Scan
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-foreground">
                      {file ? file.name : `Drop your ${docType === 'tax_return' ? 'tax return' : docType === 'bank_statement' ? 'bank statement' : 'invoice or bill'} here`}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB — Ready to extract` : 'PDF, PNG, or JPG up to 20MB'}
                    </p>
                    {file && docType === 'tax_return' && file.name.match(/\.pdf$/i) && (
                      <div className="mt-2 text-[11px] text-muted-foreground">
                        <p>By default, the first 50 pages are scanned (blank pages auto-skipped).</p>
                        <label className="flex items-center justify-center gap-2 mt-1.5 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={scanAll}
                            onChange={(e) => setScanAll(e.target.checked)}
                            className="rounded border-border text-emerald-600 focus:ring-emerald-500"
                            data-testid="scan-all-checkbox"
                          />
                          <span className={scanAll ? 'text-emerald-600 font-medium' : ''}>
                            Scan entire document {scanAll && '(may take longer)'}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                  {file && !extracting && (
                    <div className="flex gap-3 mt-2">
                      <Button onClick={(e) => { e.stopPropagation(); handleExtract(); }}
                        disabled={extracting}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" data-testid="extract-btn">
                        <ScanLine className="w-4 h-4" /> {docType === 'tax_return' ? 'Analyze Tax Return' : 'Extract Data'}
                      </Button>
                      <Button variant="outline" onClick={(e) => { e.stopPropagation(); handleReset(); }} data-testid="reset-btn">
                        <X className="w-4 h-4 mr-1" /> Clear
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tax Return Results */}
      {taxData && Object.keys(taxData).length > 0 && (
        <TaxReturnResults
          taxData={taxData}
          onReset={handleReset}
          onAnalyze={handleAnalyzeTaxReturn}
          analysis={taxAnalysis}
          analyzing={analyzing}
          analysisError={analysisError}
        />
      )}

      {/* Bank Statement Results */}
      {result && docType === 'bank_statement' && !importResult && (
        <div className="space-y-4" data-testid="extraction-results">
          {/* Summary */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="font-semibold text-foreground">Extracted {editedTransactions.length} Transactions</p>
                    <p className="text-xs text-muted-foreground">{result.account_info?.bank_name} {result.account_info?.account_last4 ? `••••${result.account_info.account_last4}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {bankAccounts.length > 0 && (
                    <select
                      value={selectedBankId}
                      onChange={(e) => setSelectedBankId(e.target.value)}
                      className="text-xs h-8 px-2 rounded-md border border-border bg-card text-foreground"
                      data-testid="bank-account-select"
                    >
                      <option value="">Auto-create account</option>
                      {bankAccounts.map(acc => (
                        <option key={acc.bank_id} value={acc.bank_id}>
                          {acc.institution_name || acc.account_name}{acc.mask ? ` ••${acc.mask}` : ''} ({acc.source || 'manual'})
                        </option>
                      ))}
                    </select>
                  )}
                  <Badge variant="outline" className="text-emerald-600">{selectedCount} selected</Badge>
                  <Button size="sm" onClick={handleImport} disabled={importing || selectedCount === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="import-btn">
                    {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                    Import {selectedCount} Transactions
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleReset} data-testid="scan-another-btn">
                    <ScanLine className="w-3 h-3 mr-1" /> Scan Another
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="transactions-table">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="p-3 text-left w-10">
                        <input type="checkbox" checked={selectedCount === editedTransactions.length}
                          onChange={() => {
                            const allSelected = selectedCount === editedTransactions.length;
                            setEditedTransactions(prev => prev.map(t => ({ ...t, _selected: !allSelected })));
                          }} className="rounded" />
                      </th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Date</th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Description</th>
                      <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Amount</th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Type</th>
                      <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editedTransactions.map((txn, idx) => (
                      <tr key={idx} className={cn(
                        "border-b border-border transition-colors",
                        txn._selected ? "bg-card" : "bg-muted/50 opacity-60"
                      )} data-testid={`txn-row-${idx}`}>
                        <td className="p-3">
                          <input type="checkbox" checked={txn._selected} onChange={() => toggleTransaction(idx)} className="rounded" />
                        </td>
                        <td className="p-3">
                          <input type="date" value={txn.date || ''} onChange={(e) => updateTransaction(idx, 'date', e.target.value)}
                            className="bg-transparent border-0 text-sm text-foreground w-32 focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded px-1" />
                        </td>
                        <td className="p-3">
                          <input type="text" value={txn.description || ''} onChange={(e) => updateTransaction(idx, 'description', e.target.value)}
                            className="bg-transparent border-0 text-sm text-foreground w-full focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded px-1" />
                        </td>
                        <td className="p-3 text-right">
                          <span className={cn("font-mono font-medium text-sm",
                            txn.type === 'credit' ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
                          )}>
                            {txn.type === 'credit' ? '+' : ''}{typeof txn.amount === 'number' ? txn.amount.toFixed(2) : txn.amount}
                          </span>
                        </td>
                        <td className="p-3">
                          <select value={txn.type || 'debit'} onChange={(e) => updateTransaction(idx, 'type', e.target.value)}
                            className="bg-transparent border-0 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded">
                            <option value="credit">Credit</option>
                            <option value="debit">Debit</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <select value={txn.category || 'Other'} onChange={(e) => updateTransaction(idx, 'category', e.target.value)}
                            className="bg-transparent border-0 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500 rounded">
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invoice Results */}
      {result && docType === 'invoice' && !importResult && (
        <div className="space-y-4" data-testid="invoice-results">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                <p className="font-semibold text-foreground">Invoice Extracted</p>
                <Button size="sm" variant="outline" onClick={handleReset}>
                  <ScanLine className="w-3 h-3 mr-1" /> Scan Another
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Vendor', value: result.vendor_name },
                  { label: 'Invoice #', value: result.invoice_number },
                  { label: 'Date', value: result.invoice_date },
                  { label: 'Total', value: result.total ? `$${Number(result.total).toFixed(2)}` : 'N/A' },
                ].map(item => (
                  <div key={item.label} className="bg-muted rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="text-sm font-semibold text-foreground mt-0.5">{item.value || 'N/A'}</p>
                  </div>
                ))}
              </div>
              {result.line_items?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm mt-4" data-testid="invoice-items-table">
                    <thead>
                      <tr className="border-b border-border bg-muted">
                        <th className="p-3 text-left text-xs font-semibold text-muted-foreground">Description</th>
                        <th className="p-3 text-center text-xs font-semibold text-muted-foreground">Qty</th>
                        <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Unit Price</th>
                        <th className="p-3 text-right text-xs font-semibold text-muted-foreground">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.line_items.map((item, idx) => (
                        <tr key={idx} className="border-b border-border">
                          <td className="p-3 text-foreground">{item.description}</td>
                          <td className="p-3 text-center text-muted-foreground">{item.quantity}</td>
                          <td className="p-3 text-right font-mono text-muted-foreground">${Number(item.unit_price || 0).toFixed(2)}</td>
                          <td className="p-3 text-right font-mono font-medium text-foreground">${Number(item.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border">
                        <td colSpan={3} className="p-3 text-right font-semibold text-foreground">Total:</td>
                        <td className="p-3 text-right font-mono font-bold text-lg text-emerald-600 dark:text-emerald-400">${Number(result.total || 0).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Success */}
      {importResult && (
        <Card data-testid="import-success">
          <CardContent className="p-8 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="text-xl font-bold text-foreground">Import Complete</p>
            <p className="text-muted-foreground">{importResult.imported_count} transactions imported successfully</p>
            <Button onClick={handleReset} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
              <ScanLine className="w-4 h-4" /> Scan Another Document
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Scan History */}
      {!extracting && !result && !taxData && !importResult && (
        <Card data-testid="scan-history">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4" /> Recent Scans
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : scanHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No scans yet. Upload a document to get started.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {scanHistory.map((job) => {
                  const statusColors = {
                    complete: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                    preparing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                    cancelled: 'bg-muted text-muted-foreground',
                    partial_error: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                  };
                  return (
                    <div
                      key={job.job_id}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg border hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => job.status === 'complete' || job.status === 'partial_error' ? loadJobResults(job.job_id) : null}
                      data-testid={`scan-history-${job.job_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${statusColors[job.status] || statusColors.failed}`}>
                          {job.status === 'processing' || job.status === 'preparing' ? (
                            <><Loader2 className="w-3 h-3 animate-spin mr-1" />{job.status}</>
                          ) : job.status}
                        </span>
                        <span className="text-sm font-medium text-foreground truncate">
                          {job.filename}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
                        <span>{job.doc_type?.replace('_', ' ')}</span>
                        <span>{job.pages_processed || 0}/{job.pages_total || '?'} pages</span>
                        <span>{new Date(job.created_at).toLocaleDateString()}</span>
                        {(job.status === 'complete' || job.status === 'partial_error') && (
                          <ArrowRight className="w-3 h-3 text-emerald-500" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export { ScannerTab };
